import React from 'react'
import { useTranslation } from 'react-i18next'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsDraggablePoints from 'highcharts/modules/draggable-points'
import HighchartsPatternFill from 'highcharts/modules/pattern-fill'

import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import ShowChartIcon from '@mui/icons-material/ShowChart'

import {
  getSeriesLabel,
  getSeriesMarkerSymbol,
  getMainProfileParam,
  isBtBasedProfile,
} from '../../utils/profileEditorUtils'
import { ProfileType } from '../../types/enums/profileType'
import {
  AxisConfig,
  generateRor,
  getDisplayTemperature,
  getGraphLimits,
  getMaxDatapointMsec,
  getPlotLineConfig,
  getSeriesColor,
  getValueSuffix,
  getXaxis,
  getYaxis,
  hexToRGBA,
} from '../../utils/graphUtils'
import { BasicDataParam, CalculatedDataParam, GraphDataParam } from '../../types/enums/graphDataParam'
import { SeriesType } from '../../types/enums/seriesType'
import { ProfileEditorSeries } from '../../types/profileEditorSeries'
import TooltipIconButton from '../fields/tooltipIconButton'
import { User } from '../../types/user'
import { generatePrecisePlotPoints } from '../../utils/precisePlot'
import { EventTypeString } from '../../types/enums/eventType'
import { GraphSeriesEvents } from '../../types/graphSeriesConfig'

// initialize draggable points
HighchartsDraggablePoints(Highcharts)
HighchartsPatternFill(Highcharts)

// all following heights are in %.
const TOP_CHART_HEIGHT = 75

// calculated values, do not change these!
const BOTTOM_CHART_TOP = TOP_CHART_HEIGHT
const BOTTOM_CHART_HEIGHT = 100 - TOP_CHART_HEIGHT

const LOG_CHART_SERIES_ID = 'series_from_log_chart'

interface ProfileEditorChartProps {
  isReadonlyProfile: boolean
  autoDropTime: number
  selectedSeries: GraphDataParam
  profileType: ProfileType
  seriesConfig: Array<ProfileEditorSeries>
  compareSeries: Array<ProfileEditorSeries>
  compareLogEvents: GraphSeriesEvents | null
  logSeries?: ProfileEditorSeries
  selectedPointIndexes: Array<number>
  minX: number
  maxX: number
  moveIntervalX: number
  onDropPoint: (param: GraphDataParam, graphIndex: number, newTime: number, newValue: number) => void
  onCreatePoint: (currentParam: GraphDataParam, newTime: number, newValue: number) => void
  setCurrentSeries: (series: GraphDataParam) => void
  selectPoint: (index: number, isAccumulate: boolean) => void
  unselectPoint: (index: number, isAccumulate: boolean) => void
  keyboardShortcutButton: JSX.Element
  user: User | null
}

const ProfileEditorChart = (props: ProfileEditorChartProps): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()
  const chartRef = React.useRef<HighchartsReact.RefObject>(null)
  const isListeningForDoubleClick = React.useRef<boolean>(false)

  const [isRorEnabled, setIsRorEnabled] = React.useState<boolean>(false)

  const getSelectedSeriesConfig = (seriesConfig: ProfileEditorSeries): Highcharts.SeriesSplineOptions => {
    const isSelectedSeries = props.selectedSeries === seriesConfig.param
    return {
      id: seriesConfig.param,
      color: getSeriesColor(seriesConfig.param, theme),
      type: 'spline',
      xAxis: getXaxis(seriesConfig.seriesType),
      yAxis: getYaxis(seriesConfig.seriesType),
      name: t(getSeriesLabel(seriesConfig.param)),
      stickyTracking: isSelectedSeries,
      zIndex: isSelectedSeries ? 2 : undefined,
      cursor: isSelectedSeries ? 'move' : undefined,
      marker: { enabled: isSelectedSeries, symbol: getSeriesMarkerSymbol(props.selectedSeries) },
      dragDrop: {
        draggableX: isSelectedSeries,
        draggableY: isSelectedSeries,
      },
      states: { hover: { enabled: isSelectedSeries }, inactive: { enabled: isSelectedSeries } },
      tooltip: { valueSuffix: getValueSuffix(seriesConfig.seriesType) },
      data: seriesConfig.data.map((x, index) => {
        return {
          x: x.msec,
          y: x.dataValue,
          selected: isSelectedSeries && props.selectedPointIndexes.indexOf(index) > -1,
        }
      }),
      zones: isBtBasedProfile(props.profileType)
        ? [
            {
              value: props.seriesConfig.find((x) => x.param === getMainProfileParam(props.profileType))?.data[1].msec,
              dashStyle: 'ShortDash',
            },
          ]
        : [],
      zoneAxis: 'x',
    }
  }

  const getTopChartConfig = (): Highcharts.XAxisOptions => {
    return {
      top: 4,
      height: `${TOP_CHART_HEIGHT}%`,
      offset: 0,
      visible: isPowerProfile() ? false : true,
    }
  }

  const getBottomChartConfig = (): Highcharts.XAxisOptions => {
    return {
      top: isPowerProfile() ? undefined : `${BOTTOM_CHART_TOP}%`,
      height: isPowerProfile() ? undefined : `${BOTTOM_CHART_HEIGHT}%`,
      offset: 0,
    }
  }

  const getXaxisConfig = (): Highcharts.XAxisOptions => {
    if (isBtBasedProfile(props.profileType)) {
      return {
        min: props.minX,
        max: props.maxX + 25,
        type: 'linear',
        title: { text: `${t('editprofileview.beantemp')} (${getValueSuffix(SeriesType.TEMPERATURE)})` },
      }
    }
    const maxMsec = Math.max(getMaxDatapointMsec(props.seriesConfig.map((x) => x.data)), 240000)
    return {
      min: props.minX,
      max: maxMsec + 60000,
      tickPixelInterval: 60,
      minTickInterval: 2000,
      type: 'datetime',
      dateTimeLabelFormats: {
        second: '%M:%S',
        minute: '%M:%S',
        hour: '%M:%S',
        day: '%M:%S',
      },
    }
  }

  const isPowerProfile = (): boolean => {
    return props.profileType === ProfileType.POWER || props.profileType === ProfileType.BT_POWER
  }

  const getPlotLinesConfig = (showLabel: boolean): Array<Highcharts.XAxisPlotLinesOptions> => {
    const plotLines = []
    ;(props.compareLogEvents?.events || []).forEach((x) => {
      const eventTemperature = getDisplayTemperature(x.bt, 1)
      const temperatureFragment = eventTemperature ? ` (${eventTemperature})` : ''
      const text = x.type !== null ? `${t(EventTypeString[x.type])}${temperatureFragment}` : ''

      plotLines.push(
        ...getPlotLineConfig(
          x.msec,
          hexToRGBA(theme.palette.plotline.main, 0.5),
          showLabel ? text : '',
          `${x.type}`,
          theme,
          0.8
        )
      )
    })

    if (props.autoDropTime)
      plotLines.push({
        value: props.autoDropTime,
        color: theme.palette.plotline.main,
        width: 2,
        id: 'plotLines',
        ...(showLabel && {
          label: {
            style: {
              color: theme.palette.text.secondary, // use default colors from MUI (easier than separate config for plotlines)
            },
            text: t('editprofileview.endtimeplotline'),
            x: 4,
          },
        }),
        zIndex: 5,
      })
    return plotLines
  }

  const getRorBaseSeries = (): ProfileEditorSeries | null => {
    return (
      props.seriesConfig.find((x) => x.param === BasicDataParam.BT) ||
      props.seriesConfig.find((x) => x.param === BasicDataParam.ET) ||
      null
    )
  }

  const generatePlotBands = (showLabel: boolean): Array<Highcharts.XAxisPlotBandsOptions> => {
    return isBtBasedProfile(props.profileType)
      ? [
          {
            from: 0,
            to: props.seriesConfig.find((x) => x.param === getMainProfileParam(props.profileType))?.data[1].msec,
            zIndex: 1,
            color: {
              pattern: {
                opacity: 0.2,
                color: theme.palette.power.main,
                path: {
                  d: 'M 0 0 L 20 20 L 100 100 Z',
                  fill: theme.palette.power.main,
                },
                width: 10,
                height: 10,
              },
            },
            ...(showLabel && {
              label: {
                text: t('editprofileview.btxaxisfirstpointhint'),
                align: 'left',
                x: 10,
                y: 20,
                style: {
                  color: theme.palette.text.secondary,
                },
              },
            }),
          },
        ]
      : []
  }

  const calculateSeries = () => {
    const series = props.seriesConfig.map((x) => getSelectedSeriesConfig(x))

    if (isRorEnabled) {
      const mainSeriesPoints = getRorBaseSeries()?.data || []
      const rorPoints = generateRor(generatePrecisePlotPoints(mainSeriesPoints), props.user)
      series.push({
        enableMouseTracking: false,
        id: CalculatedDataParam.ROR,
        name:
          getRorBaseSeries()?.param === BasicDataParam.BT
            ? t('editprofileview.btrateofrise')
            : t('editprofileview.etrateofrise'),
        color: theme.palette.ror.main,
        type: 'spline',
        xAxis: getXaxis(SeriesType.ROR),
        yAxis: getYaxis(SeriesType.ROR),
        data: rorPoints.map((x) => [x.msec, x.dataValue]),
      })
    }
    if (props.logSeries) {
      series.push({
        enableMouseTracking: false,
        id: LOG_CHART_SERIES_ID,
        name: `${t(getSeriesLabel(props.logSeries.param))} ${t('editprofileview.fromlog')}`,
        color: getSeriesColor(props.logSeries.param, theme),
        type: 'spline',
        lineWidth: 1,
        xAxis: getXaxis(props.logSeries.seriesType),
        yAxis: getYaxis(props.logSeries.seriesType),
        data: props.logSeries.data.map((x) => [x.msec, x.dataValue]),
      })
    }
    props.compareSeries.forEach((x) => {
      series.push({
        enableMouseTracking: false,
        id: `${x.param}-compare`,
        name: `${t(getSeriesLabel(x.param))} compare`,
        color: getSeriesColor(x.param, theme),
        type: 'spline',
        lineWidth: 1,
        dashStyle: 'LongDash',
        xAxis: getXaxis(x.seriesType),
        yAxis: getYaxis(x.seriesType),
        data: x.data.map((x) => [x.msec, x.dataValue]),
      })
    })

    return series
  }

  const options = {
    title: { text: '' },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            chart: {
              spacing: [0, 0, 0, 0],
            },
          },
        },
      ],
    },
    accessibility: {
      enabled: false,
    },
    chart: { type: 'line' },
    credits: { enabled: false },
    exporting: false,
    tooltip: {
      ...(isBtBasedProfile(props.profileType) && {
        // TODO: tooltip formatter to show START instead of 0 msec
        headerFormat: `${t('editprofileview.beantemp')}: {point.key} ${getValueSuffix(SeriesType.TEMPERATURE)}<br>`,
      }),
      xDateFormat: `${t('editprofileview.time')}: %M:%S`,
    },
    xAxis: [
      {
        ...getTopChartConfig(),
        ...getXaxisConfig(),
        id: AxisConfig.X_AXIS_TOP,
        labels: { enabled: false },
        plotLines: getPlotLinesConfig(true),
        title: { text: '' },
        plotBands: generatePlotBands(true),
      },
      {
        ...getBottomChartConfig(),
        ...getXaxisConfig(),
        id: AxisConfig.X_AXIS_BOTTOM,
        linkedTo: isPowerProfile() ? undefined : 0,
        labels: { y: isPowerProfile() ? 25 : -3 },
        opposite: isPowerProfile() ? false : true,
        plotLines: getPlotLinesConfig(isPowerProfile()),
        plotBands: generatePlotBands(isPowerProfile()),
      },
    ], //xAxis
    yAxis: [
      {
        // temperature series - yaxis = 0
        ...getTopChartConfig(),
        id: AxisConfig.Y_AXIS_TEMPERATURE,
        min: getGraphLimits(getMainProfileParam(props.profileType)).min,
        max: getGraphLimits(getMainProfileParam(props.profileType)).max + 35,
        alignTicks: false,
        tickInterval: 25,
        title: { text: `${t('editprofileview.temperature')} (${getValueSuffix(SeriesType.TEMPERATURE)})` },
      },
      {
        // Power & Fan - yaxis = 1
        ...getBottomChartConfig(),
        id: AxisConfig.Y_AXIS_PERCENT,
        min: getGraphLimits(BasicDataParam.POWER).min,
        max: getGraphLimits(BasicDataParam.POWER).max,
        alignTicks: false,
        tickInterval: 25,
        title: { text: t('editprofileview.percentage') },
      },
      {
        // Ror - yaxis = 3
        ...getTopChartConfig(),
        id: AxisConfig.Y_AXIS_ROR,
        min: getGraphLimits(CalculatedDataParam.ROR).min,
        max: getGraphLimits(CalculatedDataParam.ROR).max + 5,
        lineWidth: 0,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        tickInterval: 10,
        alignTicks: false,
        opposite: true,
        title: {
          text: `${t('editprofileview.rateofrise')} (${getValueSuffix(SeriesType.ROR)})`,
        },
      },
    ],
    plotOptions: {
      series: {
        clip: false,
        allowPointSelect: true,
        findNearestPointBy: 'xy',
        ...(navigator.maxTouchPoints > 0 && {
          marker: {
            radius: 10,
          },
        }),
        dragDrop: {
          // We must select a series before drag/drop is enabled.
          dragPrecisionX: props.moveIntervalX,
          dragPrecisionY: 1,
          dragSensitivity: 2,
        },
        events: {
          click: (clickEvent: Highcharts.SeriesClickEventObject & Highcharts.PointerEventObject): void => {
            if (
              clickEvent.point.series.options.id !== CalculatedDataParam.ROR &&
              clickEvent.point.series.options.id !== LOG_CHART_SERIES_ID
            ) {
              // ROR spline cannot be selected; it is a calculated series, not a list of editable points
              const currentSeries = clickEvent.point.series.options.id as GraphDataParam
              const seriesOptions = props.seriesConfig.find((x) => x.param === currentSeries)
              props.setCurrentSeries(currentSeries)
              if (
                (isListeningForDoubleClick.current || clickEvent.ctrlKey || clickEvent.metaKey) &&
                chartRef.current &&
                seriesOptions &&
                !props.isReadonlyProfile
              ) {
                const xAxis = chartRef.current.chart.xAxis.find(
                  (x: Highcharts.Axis) => x.options.id === getXaxis(seriesOptions.seriesType)
                )
                const yAxis = chartRef.current.chart.yAxis.find(
                  (x: Highcharts.Axis) => x.options.id === getYaxis(seriesOptions.seriesType)
                )
                if (xAxis && yAxis) {
                  const x = Math.round(xAxis.toValue(clickEvent.chartX))
                  const y = Math.round(yAxis.toValue(clickEvent.chartY))
                  clickEvent.point.onMouseOut() // do not select nearest existing point
                  props.onCreatePoint(currentSeries, x, y)
                }
              }
              if (!isListeningForDoubleClick.current) {
                isListeningForDoubleClick.current = true
                setTimeout(() => {
                  isListeningForDoubleClick.current = false
                }, 400)
              }
            }
          },
        },
        point: {
          events: {
            drop: (dragEvent: { target: Highcharts.Point; newPoint: Highcharts.Point }): boolean => {
              if (props.isReadonlyProfile) {
                return false
              }
              const pointIndex = dragEvent.target.index
              const currentSeries = dragEvent.target.series.options.id as GraphDataParam
              props.onDropPoint(currentSeries, pointIndex, dragEvent.newPoint.x, dragEvent.newPoint.y || 0)
              return false
            },
            unselect: (selectEvent: { target: Highcharts.Point; accumulate: boolean }): void => {
              if (document.activeElement && (document.activeElement as HTMLElement).blur) {
                // deselect any focused form elements when point is selected
                ;(document.activeElement as HTMLElement).blur()
              }
              props.unselectPoint(selectEvent.target.index, selectEvent.accumulate)
            },
            select: (selectEvent: { target: Highcharts.Point; accumulate: boolean }): void => {
              if (document.activeElement && (document.activeElement as HTMLElement).blur) {
                // deselect any focused form elements when point is selected
                ;(document.activeElement as HTMLElement).blur()
              }
              props.selectPoint(selectEvent.target.index, selectEvent.accumulate)
            },

            update: (dragEvent: { target: Highcharts.Point; options: Highcharts.Point }): boolean | void => {
              if (props.isReadonlyProfile) {
                return false
              }
              const index = dragEvent.target.index

              // The first point can't move on x axis!
              if (index === 0) {
                dragEvent.options.x = props.minX
              }
              // We must stop points from moving beyond prev/next point!
              const length = dragEvent.target.series.data.length
              const x_max =
                index + 1 < length ? dragEvent.target.series.data[index + 1].x - props.moveIntervalX : props.maxX
              const x_min = index - 1 > 0 ? dragEvent.target.series.data[index - 1].x + props.moveIntervalX : props.minX

              if (dragEvent.options.x > x_max) {
                dragEvent.options.x = x_max
              } else if (dragEvent.options.x < x_min) {
                dragEvent.options.x = x_min
              }

              // handle min + max temp
              const currentSeries = dragEvent.target.series.options.id as GraphDataParam
              const y_min = getGraphLimits(currentSeries).min
              const y_max = getGraphLimits(currentSeries).max

              if ((dragEvent.options.y || 0) < y_min) {
                dragEvent.options.y = y_min
              } else if ((dragEvent.options.y || 0) > y_max) {
                dragEvent.options.y = y_max
              }
            },
          },
        },
      },
    }, // plotOptions
    series: calculateSeries(),
  }

  return (
    <>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
        containerProps={{ style: { height: '100%', flex: 1 } }}
      />
      <Stack sx={{ position: 'absolute', right: 0, mt: '-5px' }}>
        {getRorBaseSeries() && props.user && (
          <TooltipIconButton
            onClick={() => setIsRorEnabled(!isRorEnabled)}
            tooltip={t('editprofileview.showror')}
            icon={<ShowChartIcon />}
            isToggled={isRorEnabled}
          />
        )}
        {props.keyboardShortcutButton}
      </Stack>
    </>
  )
}

export default ProfileEditorChart
